import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getRemainingDays } from '../lib/access';
import { 
  LayoutDashboard, 
  FileQuestion, 
  History, 
  BarChart3, 
  Shield, 
  LogOut,
  Menu,
  X,
  User,
  ChevronDown,
  ClipboardCheck,
  CircleHelp
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Layout = ({ children }) => {
  const { user, logoutUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  // Compras renovadas iniciam um novo período; contas antigas usam o cadastro.
  const daysLeft = getRemainingDays(user?.access_started_at || user?.created_at, new Date(), user?.access_expires_at);
  // BLOQUEIO AUTOMÁTICO:
  // Se não for admin e o tempo acabou, desloga o usuário imediatamente
  if (user && !user.is_admin && daysLeft <= 0) {
    logoutUser();
    navigate('/login');
    alert("Seu período de acesso expirou. Entre em contato com o suporte se você acha que isso é um erro.");
    return null; 
  }

  const navItems = [
    { path: '/dashboard', label: 'Matérias', icon: LayoutDashboard },
    { path: '/history', label: 'Histórico', icon: History },
    { path: '/stats', label: 'Estatísticas', icon: BarChart3 },
    { path: '/help', label: 'Ajuda', icon: CircleHelp },
  ];

  const adminItems = [
    { path: '/admin/questions', label: 'Questões', icon: FileQuestion },
    { path: '/admin/question-reviews', label: 'Revisões', icon: ClipboardCheck },
    { path: '/admin/users', label: 'Usuários', icon: User },
  ];

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`sidebar-link ${isActive ? 'active' : ''}`}
      >
        <item.icon className="w-5 h-5" strokeWidth={1.5} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="flex min-h-screen bg-zinc-100">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 bg-zinc-900 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img src="/logo-voar.png" alt="Simulados V.O.A.R." className="h-8 invert" />
          <span className="text-white font-bold text-sm">Simulados V.O.A.R.</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-200 lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full bg-zinc-900">
          {/* Logo Section */}
          <div className="h-16 flex items-center gap-3 px-4 border-b border-zinc-800">
            <img src="/logo-voar.png" alt="Simulados V.O.A.R." className="h-10 invert" />
            <div>
              <h1 className="text-white font-bold text-sm">Simulados V.O.A.R.</h1>
              <p className="text-xs text-zinc-500">Método V.O.A.R.</p>
            </div>
          </div>

          {/* User Section no Topo - Corrigido */}
          <div className="p-4 border-b border-zinc-800">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-zinc-800 transition-colors">
                  <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-zinc-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm text-white truncate">{user?.email}</p>
                    <p className="text-xs font-medium">
                      {user?.is_admin ? (
                        <span className="text-amber-500">Acesso Vitalício</span>
                      ) : (
                        <span className="text-zinc-500">{daysLeft} dias restantes</span>
                      )}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-zinc-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/change-password')}>
                  Alterar Senha
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            <p className="text-xs text-zinc-500 uppercase tracking-wider px-4 mb-2">Menu</p>
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} onClick={() => setSidebarOpen(false)} />
            ))}

            {user?.is_admin && (
              <>
                <div className="pt-4 mt-4 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider px-4 mb-2 flex items-center gap-2">
                    <Shield className="w-3 h-3" /> Admin
                  </p>
                </div>
                {adminItems.map((item) => (
                  <NavLink key={item.path} item={item} onClick={() => setSidebarOpen(false)} />
                ))}
              </>
            )}
          </nav>
        </div>
      </aside>

      <main className="flex-1 min-w-0 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
